import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { User, Globe, Crown, Save, LogOut, FolderOpen, ChevronRight, Brain, Trash2, Pencil, Plus, X, Check } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { notifyError } from "@/lib/api";
import { PLANS } from "@/lib/plans";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SectionCard = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <h2 className="flex items-center gap-2 font-display text-base font-bold text-slate-900">
      <Icon className="h-4 w-4 text-primary" /> {title}
    </h2>
    <div className="mt-4">{children}</div>
  </div>
);

export default function Settings() {
  const { t, language, setLanguage } = useLanguage();
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  const tier = user?.membership_tier || "free";
  const tierName = (PLANS[tier] || PLANS.free).name[language] || (PLANS[tier] || PLANS.free).name.en;

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { name: name.trim() });
      setUser((prev) => ({ ...prev, ...data }));
      toast.success(t("saved_changes"));
    } catch (e) {
      notifyError(e, t);
    } finally {
      setSaving(false);
    }
  };

  const changeLang = async (lang) => {
    setLanguage(lang);
    try { await api.put("/preferences", { language: lang }); } catch { /* ignore */ }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-2xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl" data-testid="settings-title">{t("settings_title")}</h1>
        <p className="mt-1.5 text-slate-500">{t("settings_subtitle")}</p>

        <div className="mt-8 space-y-5">
          <SectionCard icon={User} title={t("account_section")}>
            <div className="space-y-4">
              <div>
                <Label>{t("profile_name")}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" className="mt-1.5 min-h-[48px]" />
              </div>
              <div>
                <Label>{t("email")}</Label>
                <Input value={user?.email || ""} disabled className="mt-1.5 min-h-[48px] bg-slate-50" data-testid="settings-email" />
              </div>
              <Button onClick={saveProfile} disabled={saving} data-testid="settings-save-btn" className="gap-1.5 rounded-full min-h-[44px]">
                <Save className="h-4 w-4" /> {saving ? t("loading") : t("save_changes")}
              </Button>
            </div>
          </SectionCard>

          <SectionCard icon={Globe} title={t("language_label")}>
            <div className="flex gap-2" data-testid="settings-language">
              {[["en", "English"], ["hi", "हिंदी"]].map(([code, label]) => (
                <button key={code} onClick={() => changeLang(code)} data-testid={`settings-lang-${code}`}
                  className={`min-h-[44px] flex-1 rounded-xl border px-4 text-sm font-medium transition-colors ${language === code ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-600 hover:border-primary/40"}`}>
                  {label}
                </button>
              ))}
            </div>
          </SectionCard>

          <SectionCard icon={Crown} title={t("membership_section")}>
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600" data-testid="settings-membership-status">
                {t("you_are_on")} EVERMIN {tierName}
              </p>
              <Button variant="outline" onClick={() => navigate("/membership")} data-testid="settings-membership-btn" className="gap-1 rounded-full">
                {t("manage_plan")} <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </SectionCard>

          <SectionCard icon={FolderOpen} title={t("saved_data_section")}>
            <p className="text-sm text-slate-500">{t("saved_data_desc")}</p>
            <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="settings-dashboard-btn" className="mt-3 gap-1 rounded-full">
              {t("open_dashboard")} <ChevronRight className="h-4 w-4" />
            </Button>
          </SectionCard>

          <SectionCard icon={Brain} title={t("memory_section")}>
            <MemorySection />
          </SectionCard>

          <Button variant="outline" onClick={doLogout} data-testid="settings-logout-btn" className="w-full gap-1.5 rounded-xl min-h-[48px] text-rose-600 hover:bg-rose-50 hover:text-rose-700">
            <LogOut className="h-4 w-4" /> {t("logout_section")}
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function MemorySection() {
  const { t } = useLanguage();
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/memories").then(({ data }) => setMemories(data)).catch(() => setMemories([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const text = draft.trim();
    if (text.length < 2) return;
    setAdding(true);
    try {
      const { data } = await api.post("/memories", { text });
      setMemories((prev) => [{ ...data, updated_at: new Date().toISOString() }, ...prev]);
      setDraft("");
      toast.success(t("memory_saved"));
    } catch (e) { notifyError(e, t); } finally { setAdding(false); }
  };

  const saveEdit = async (id) => {
    const text = editText.trim();
    if (text.length < 2) return;
    try {
      await api.put(`/memories/${id}`, { text });
      setMemories((prev) => prev.map((m) => (m.memory_id === id ? { ...m, text } : m)));
      setEditId(null);
      toast.success(t("memory_saved"));
    } catch (e) { notifyError(e, t); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/memories/${id}`);
      setMemories((prev) => prev.filter((m) => m.memory_id !== id));
      toast.success(t("memory_deleted"));
    } catch (e) { notifyError(e, t); }
  };

  const clearAll = async () => {
    if (!window.confirm(t("memory_clear_confirm"))) return;
    try {
      await api.delete("/memories");
      setMemories([]);
      toast.success(t("memory_cleared"));
    } catch (e) { notifyError(e, t); }
  };

  return (
    <div data-testid="memory-section">
      <p className="text-sm text-slate-500">{t("memory_desc")}</p>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder={t("memory_add_placeholder")}
          data-testid="memory-add-input"
          className="min-h-[48px]"
          maxLength={240}
        />
        <Button onClick={add} disabled={adding || draft.trim().length < 2} data-testid="memory-add-btn" className="gap-1.5 rounded-full min-h-[44px] sm:w-auto">
          <Plus className="h-4 w-4" /> {t("memory_add_btn")}
        </Button>
      </div>

      <div className="mt-4 space-y-2" data-testid="memory-list">
        {loading ? (
          <div className="h-12 animate-pulse rounded-xl bg-slate-100" />
        ) : memories.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-6 text-center text-sm text-slate-500" data-testid="memory-empty">
            {t("memory_empty")}
          </p>
        ) : (
          memories.map((m) => (
            <div key={m.memory_id} data-testid={`memory-item-${m.memory_id}`}
              className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
              {editId === m.memory_id ? (
                <>
                  <Input value={editText} onChange={(e) => setEditText(e.target.value)} data-testid="memory-edit-input" className="min-h-[40px] flex-1" maxLength={240} />
                  <Button size="icon" variant="ghost" onClick={() => saveEdit(m.memory_id)} data-testid={`memory-save-${m.memory_id}`} className="h-9 w-9 text-emerald-600 hover:bg-emerald-50">
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setEditId(null)} className="h-9 w-9 text-slate-400 hover:bg-slate-100">
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm text-slate-700">{m.text}</span>
                  <Button size="icon" variant="ghost" onClick={() => { setEditId(m.memory_id); setEditText(m.text); }} data-testid={`memory-edit-${m.memory_id}`} className="h-9 w-9 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(m.memory_id)} data-testid={`memory-delete-${m.memory_id}`} className="h-9 w-9 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {memories.length > 0 && (
        <Button variant="ghost" onClick={clearAll} data-testid="memory-clear-all-btn" className="mt-3 gap-1.5 text-rose-600 hover:bg-rose-50">
          <Trash2 className="h-4 w-4" /> {t("memory_clear_all")}
        </Button>
      )}
    </div>
  );
}
